import React, { useEffect, useState } from "react";
import { api, type AppNotification } from "../../api";
import { useLanguage } from "../../i18n";
import { BellIcon } from "./Icons";
import { parseNavTarget } from "../../pendingNav";

export function NotificationBell({ unreadCount, onClick }: { unreadCount: number; onClick: () => void }) {
  const { t } = useLanguage();
  return (
    <button
      className="icon-btn"
      style={{ position: "relative" }}
      onClick={onClick}
      aria-label={t("notifications.title")}
      title={t("notifications.title")}
    >
      <span style={{ width: 19, height: 19, display: "block" }}>
        <BellIcon />
      </span>
      {unreadCount > 0 && <span className="nav-badge" style={{ position: "absolute", top: 6, right: 6 }} />}
    </button>
  );
}

export function NotificationsScreen({
  onBack,
  onChanged,
  onNavigate,
}: {
  onBack: () => void;
  onChanged: () => void;
  onNavigate: (view: string, tab?: string) => void;
}) {
  const { t } = useLanguage();
  const [items, setItems] = useState<AppNotification[] | null>(null);

  async function load() {
    const { notifications } = await api.get("/api/notifications/mine");
    setItems(notifications);
  }

  useEffect(() => {
    load();
  }, []);

  async function markAllRead() {
    await api.post("/api/notifications/read-all", {});
    load();
    onChanged();
  }

  // Tapping a notification always takes you to the screen it's about
  // (visit/maintenance updates -> Requests, rent updates -> Payments, etc.)
  // — marking it read is a side effect, not the point of the tap.
  async function openItem(n: AppNotification) {
    if (!n.read) {
      await api.patch(`/api/notifications/${n.id}`, {});
      setItems((prev) => (prev ? prev.map((x) => (x.id === n.id ? { ...x, read: true } : x)) : prev));
      onChanged();
    }
    if (n.url) {
      const { view, tab } = parseNavTarget(n.url);
      onNavigate(view, tab);
    }
  }

  const hasUnread = !!items?.some((n) => !n.read);

  return (
    <>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
        <button className="icon-btn" onClick={onBack} aria-label="Back">‹</button>
        <div className="section-title" style={{ marginBottom: 0, flex: 1 }}>{t("notifications.title")}</div>
        {hasUnread && <small onClick={markAllRead} style={{ cursor: "pointer", fontWeight: 700, color: "var(--brand-700)", fontSize: 12.5 }}>{t("notifications.markAllRead")}</small>}
      </div>
      {items === null && <div className="spinner" />}
      {items?.length === 0 && <div className="empty-state">{t("notifications.emptyState")}</div>}
      {items?.map((n) => (
        <div
          key={n.id}
          className="card"
          style={{ cursor: "pointer", opacity: n.read ? 0.72 : 1 }}
          onClick={() => openItem(n)}
        >
          <div className="card-row">
            <b>{n.title}</b>
            {!n.read && <span className="nav-badge" style={{ position: "static", flexShrink: 0 }} />}
          </div>
          <div className="sub" style={{ marginTop: 4 }}>{n.body}</div>
          <div className="sub" style={{ marginTop: 6, fontSize: 11 }}>
            {new Date(n.createdAt).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" })}
          </div>
        </div>
      ))}
    </>
  );
}
